/**
 * Semantic Color Tokens
 *
 * Color tokens organized by their semantic meaning and usage context.
 * All hex values are exact matches from the Figma variable collection.
 *
 * Based on Figma variable collection: Semantic Color
 */

export const backgroundColors = {
  "Background/White": "#ffffff",
  "Background/Bluish": "#dae8ff",
  "Background/Default": "#0566FF",
  "Background/Dark Grey": "#e1e6ee",
  "Background/Greyish": "#f6f7f9",
  "Background/Med Grey": "#ebedf0",
  "Background/Light Blue": "#ecf3ff",
  "Background/Red Light": "#fee4e1",
  "Background/WhatsApp": "#def8e8",
  "Background/Query": "#fff3d9",
  "Background/Tasks": "#e3e8fb",
  "Background/Documents": "#fee9e7",
} as const;

export const textColors = {
  "Text/Black": "#080f1a",
  "Text/Grey": "#354869",
  "Text/Light Grey": "#647491",
  "Text/Primary": "#0566ff",
  "Text/Red": "#f94839",
} as const;

export const primaryColors = {
  "Primary/Default": "#0566ff",
  "Primary/Disabled": "#9BC2FF",
  "Alerts/Green": "#08A742",
} as const;

export const alertColors = {
  "Alert/Red/Dark": "#f94839",
  "Alert/Red/Light": "#fee4e1",
  "Alert/Yellow/Dark": "#ffd700",
  "Alert/Green/Dark": "#08a742",
  "Alert/Green/Light": "#daf2e3",
} as const;

export const statusColors = {
  "Status/Open/Bg": "#dae8ff",
  "Status/Open/Text": "#0566ff",
  "Status/Won/Bg": "#daf2e3",
  "Status/Won/Text": "#08a742",
  "Status/Lost/Bg": "#fee4e1",
  "Status/Lost/Text": "#f94839",
} as const;

export const borderColors = {
  "Borders/Light": "#d4dbe5",
  "Borders/Light Grey": "#E9EDF2",
} as const;

// Combined color tokens
export const colorTokens = {
  ...backgroundColors,
  ...textColors,
  ...primaryColors,
  ...alertColors,
  ...statusColors,
  ...borderColors,
} as const;

// Type exports for TypeScript support
export type BackgroundColorToken = keyof typeof backgroundColors;
export type TextColorToken = keyof typeof textColors;
export type PrimaryColorToken = keyof typeof primaryColors;
export type AlertColorToken = keyof typeof alertColors;
export type StatusColorToken = keyof typeof statusColors;
export type BorderColorToken = keyof typeof borderColors;
export type ColorToken = keyof typeof colorTokens;
export type ColorValue = (typeof colorTokens)[keyof typeof colorTokens];

// RGB values for programmatic use
export const colorRGB = {
  "Background/White": { r: 255, g: 255, b: 255 },
  "Background/Bluish": { r: 218, g: 232, b: 255 },
  "Background/Dark Grey": { r: 225, g: 230, b: 238 },
  "Background/Greyish": { r: 246, g: 247, b: 249 },
  "Background/Med Grey": { r: 235, g: 237, b: 240 },
  "Background/Light Blue": { r: 236, g: 243, b: 255 },
  "Background/Red Light": { r: 254, g: 228, b: 225 },
  "Background/WhatsApp": { r: 222, g: 248, b: 232 },
  "Background/Query": { r: 255, g: 243, b: 217 },
  "Background/Tasks": { r: 227, g: 232, b: 251 },
  "Background/Documents": { r: 254, g: 233, b: 231 },
  "Text/Black": { r: 8, g: 15, b: 26 },
  "Text/Grey": { r: 53, g: 72, b: 105 },
  "Text/Light Grey": { r: 100, g: 116, b: 145 },
  "Text/Primary": { r: 5, g: 102, b: 255 },
  "Text/Red": { r: 249, g: 72, b: 57 },
  "Primary/Default": { r: 5, g: 102, b: 255 },
  "Primary/Disabled": { r: 155, g: 194, b: 255 },
  "Alert/Red/Dark": { r: 249, g: 72, b: 57 },
  "Alert/Red/Light": { r: 254, g: 228, b: 225 },
  "Alert/Yellow/Dark": { r: 255, g: 215, b: 0 },
  "Alert/Green/Dark": { r: 8, g: 167, b: 66 },
  "Alert/Green/Light": { r: 218, g: 242, b: 227 },
  "Status/Open/Bg": { r: 218, g: 232, b: 255 },
  "Status/Open/Text": { r: 5, g: 102, b: 255 },
  "Status/Won/Bg": { r: 218, g: 242, b: 227 },
  "Status/Won/Text": { r: 8, g: 167, b: 66 },
  "Status/Lost/Bg": { r: 254, g: 228, b: 225 },
  "Status/Lost/Text": { r: 249, g: 72, b: 57 },
  "Borders/Light": { r: 212, g: 219, b: 229 },
} as const;

// Helper function to get color value
export const getColor = (token: ColorToken): ColorValue => {
  return colorTokens[token];
};

// Helper function to get RGB values
export const getColorRGB = (token: ColorToken) => {
  return colorRGB[token as keyof typeof colorRGB];
};

// Helper function to convert hex to RGB string
export const hexToRgb = (hex: string): string => {
  const token = hex as ColorToken;
  if (token in colorRGB) {
    const rgb = colorRGB[token as keyof typeof colorRGB];
    return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
  }
  // Fallback for any hex not in our tokens
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    return `rgb(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)})`;
  }
  return hex;
};
